import React, { useCallback, useEffect, useState } from 'react';
import { getUrlParameter, Translate, translate, ValidatedForm } from 'react-jhipster';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { Login } from 'app/shared/layout/login/Login';
import PasswordStrengthBar from 'app/shared/layout/password/password-strength-bar';
import { TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import { handlePasswordResetFinish, reset } from '../password-reset.reducer';

interface FormData {
  newPassword: string;
  confirmPassword: string;
}

export const PasswordResetFinishPage = (props: RouteComponentProps<{ key: string }>) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<FormData>({
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({} as FormData);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [key] = useState(getUrlParameter('key', props.location.search));
  const successMessage = useAppSelector(state => state.passwordReset.successMessage);

  useEffect(
    () => () => {
      dispatch(reset());
    },
    []
  );

  const validateForm = useCallback(() => {
    const newErrors = {} as FormData;
    if (!formData.newPassword) newErrors.newPassword = translate('fleetSenseApp.password.errors.required');
    if (!formData.confirmPassword) newErrors.confirmPassword = translate('fleetSenseApp.password.errors.required');
    if (formData.newPassword !== formData.confirmPassword) newErrors.confirmPassword = translate('fleetSenseApp.password.errors.dontMatch');
    if (formData.newPassword.length < 4) newErrors.newPassword = translate('fleetSenseApp.password.errors.minLength', { min: 4 });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleValidSubmit = useCallback(
    _ => {
      if (validateForm()) {
        dispatch(
          handlePasswordResetFinish({
            key,
            newPassword: formData.newPassword,
          })
        );
      }
    },
    [dispatch, formData, key, validateForm]
  );

  const handleInputChange = (fieldName: keyof FormData, value: string) => {
    setFormData({ ...formData, [fieldName]: value });
  };

  useEffect(() => {
    if (successMessage) {
      toast.success(translate(successMessage), { autoClose: 10000 });
    }
  }, [successMessage]);

  return (
    <Login>
      {successMessage && <Redirect to={'/'} />}
      <Typography color="secondary" fontWeight={'700'} component="h1" variant="h5" align="center" sx={{ mb: { xs: 4, md: 6 }, mt: { xs: 6, md: 8 } }}>
        <Translate contentKey="fleetSenseApp.reset.request.title">Reset password</Translate>
      </Typography>
      <Grid container spacing={3} sx={{ minHeight: '380px', position: 'relative' }}>
        <Grid item xs={12} sm={12}>
          <ValidatedForm onSubmit={handleValidSubmit}>
            <TextField
              name="newPassword"
              type={showPassword ? 'text' : 'password'}
              label={translate('global.form.newpassword.label')}
              placeholder={translate('global.form.newpassword.placeholder')}
              variant="outlined"
              required
              autoFocus
              fullWidth
              color="secondary"
              data-cy="resetPassword"
              value={formData.newPassword}
              onChange={e => handleInputChange('newPassword', e.target.value)}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              inputProps={{
                maxLength: 50,
                style: { color: 'primary' },
              }}
              InputProps={{
                style: { borderRadius: '8px', marginRight: '8px' },
                endAdornment: (
                  <InputAdornment position="end" style={{ marginRight: '5px' }}>
                    <IconButton edge="end" onClick={() => setShowPassword(!showPassword)} color="secondary">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              label={translate('global.form.confirmpassword.label')}
              placeholder={translate('global.form.confirmpassword.placeholder')}
              variant="outlined"
              required
              fullWidth
              color="secondary"
              data-cy="confirmResetPassword"
              value={formData.confirmPassword}
              onChange={e => handleInputChange('confirmPassword', e.target.value)}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              inputProps={{
                maxLength: 50,
                style: { color: 'primary' },
              }}
              InputProps={{
                style: { borderRadius: '8px', marginRight: '8px' },
                endAdornment: (
                  <InputAdornment position="end" style={{ marginRight: '5px' }}>
                    <IconButton edge="end" onClick={() => setShowConfirmPassword(!showConfirmPassword)} color="secondary">
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                my: { xs: 4 },
              }}
            />
            <TypographyForms>
              <Translate contentKey="global.messages.validate.newpassword.strength">Password strength</Translate>
            </TypographyForms>
            <PasswordStrengthBar password={formData.newPassword} />
            <Box sx={{ justifyContent: 'flex-end', padding: '24px 0px 24px 24px', position: 'absolute', bottom: 0, right: 0, width: '100%' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                color="secondary"
                data-cy="submit"
                fullWidth
                sx={{
                  fontWeight: '800',
                }}
              >
                <Translate contentKey="fleetSenseApp.reset.finish.form.button">Validate new password</Translate>
              </Button>
            </Box>
          </ValidatedForm>
        </Grid>
      </Grid>
    </Login>
  );
};

export default PasswordResetFinishPage;
